import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectLoggedInUserId } from "../../../../Store/features/user/userSlice";
import { getAllCommunity } from "../../../../Service/user";
import DatePicker from "react-datepicker";
import {
	addMinutes,
	format,
	set,
	setHours,
	setMinutes,
	setSeconds,
} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./CreateWebinarModal.scss";
import { Modal, Button } from "react-bootstrap";

const WebinarModal = ({ isOpen, onClose, onSubmit, initialData, isEditing }) => {
	const loggedInUserId = useSelector(selectLoggedInUserId);
	// const initStartTime = setSeconds(setMinutes(setHours(new Date(), 9), 0), 0);
	const [formData, setFormData] = useState({
		title: "",
		description: "",
		duration: 30,
		webinarType: "Public",
		price: 0,
		discount: 0,
		date: new Date(),
		startTime: new Date(),
		endTime: addMinutes(new Date(), 30),
		image: null,
	});

	const [previewImage, setPreviewImage] = useState(null);
	const [community, setCommunity] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// console.log("community", community);

	useEffect(() => {
		setLoading(true);
		getAllCommunity(loggedInUserId)
			.then((res) => {
				setCommunity(res.data);
				setLoading(false);
			})
			.catch((err) => {
				setError(err);
			})
			.finally(() => setLoading(false));
	}, [loggedInUserId]);

	// Add Quill modules and formats configuration
	const modules = {
		toolbar: [
			[{ header: [1, 2, 3, 4, 5, 6, false] }],
			["bold", "italic", "underline", "strike"],
			[{ list: "ordered" }, { list: "bullet" }],
			["link"],
			["clean"],
		],
	};

	const formats = [
		"header",
		"bold",
		"italic",
		"underline",
		"strike",
		"list",
		"bullet",
		"link",
	];

	// Helper function to format date and time in ISO format
	const formatDateTime = (date, time) => {
		// Create new Date objects to avoid mutating the originals
		const combinedDate = new Date(date);
		const timeDate = new Date(time);

		// Set the hours, minutes, and reset seconds/milliseconds
		combinedDate.setHours(timeDate.getHours());
		combinedDate.setMinutes(timeDate.getMinutes());
		combinedDate.setSeconds(0);
		combinedDate.setMilliseconds(0);

		return combinedDate;
	};

	// Helper function to calculate end time
	const calculateEndTime = (startTime, duration) => {
		return addMinutes(new Date(startTime), parseInt(duration));
	};

	// Update end time whenever start time or duration changes
	useEffect(() => {
		setFormData((prev) => ({
			...prev,
			endTime: calculateEndTime(prev.startTime, prev.duration),
		}));
	}, [formData.startTime, formData.duration]);

	// Update initialization when editing
	useEffect(() => {
		if (isEditing && initialData) {
			try {
				const startDateTime = new Date(initialData.startTime);
				const endDateTime = new Date(initialData.endTime);
				
				setFormData({
					title: initialData.title || "",
					description: initialData.description || "",
					duration: initialData.duration || 30,
					webinarType: initialData.webinarType || "Public",
					price: initialData.price || 0,
					discount: initialData.discount || 0,
					date: new Date(initialData.date),
					startTime: startDateTime,
					endTime: endDateTime,
					image: initialData.image || null,
				});
			} catch (error) {
				console.error("Error parsing dates:", error);
			}
		}
	}, [isEditing, initialData]);

	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			setFormData({
				title: "",
				description: "",
				duration: 30,
				webinarType: "Public",
				price: 0,
				discount: 0,
				date: new Date(),
				startTime: new Date(),
				endTime: addMinutes(new Date(), 30),
				image: null,
			});
			setPreviewImage(null);
		}
	}, [isOpen]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			// Create preview URL
			const reader = new FileReader();
			reader.onloadend = () => {
				// Store the base64 string in formData.image
				setFormData((prev) => ({
					...prev,
					image: reader.result, // This stores the base64 string
				}));
				setPreviewImage(reader.result);
			};
			reader.readAsDataURL(file);
		}
	};

	const handleDateChange = (date) => {
		if (!date) return;

		setFormData((prev) => {
			const newDate = new Date(date);
			// Preserve the time from the existing startTime
			const currentStartTime = new Date(prev.startTime);
			newDate.setHours(currentStartTime.getHours());
			newDate.setMinutes(currentStartTime.getMinutes());

			return {
				...prev,
				date: newDate,
				startTime: newDate,
				endTime: addMinutes(newDate, parseInt(prev.duration)),
			};
		});
	};

	const handleTimeChange = (time) => {
		if (!time) return;

		setFormData((prev) => {
			const newStartTime = new Date(time);
			const newEndTime = addMinutes(newStartTime, parseInt(prev.duration));
			
			return {
				...prev,
				startTime: newStartTime,
				endTime: newEndTime,
			};
		});
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		// Format all dates to ISO strings for submission
		const formattedStartTime = formatDateTime(formData.date, formData.startTime);
		const formattedEndTime = addMinutes(formattedStartTime, parseInt(formData.duration));

		const formattedData = {
			...formData,
			date: formData.date.toISOString(),
			startTime: formattedStartTime.toISOString(),
			endTime: formattedEndTime.toISOString(),
		};

		onSubmit(formattedData);
	};

	const handleQuillChange = (value) => {
		setFormData((prev) => ({
			...prev,
			description: value,
		}));
	};

	if (!isOpen) return null;

	return (
		<Modal show={isOpen} onHide={onClose}>
			<Modal.Header closeButton>
				<Modal.Title>{isEditing ? 'Edit Webinar' : 'Create Webinar'}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<form onSubmit={handleSubmit}>
					<div className="form-group image-upload">
						<div className="image-upload-container">
							{previewImage ? (
								<div className="image-preview">
									<img src={previewImage} alt="Webinar preview" />
								</div>
							) : (
								<label htmlFor="image" className="image-placeholder">
									<div className="placeholder-icon">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
											<polyline points="7 10 12 15 17 10"></polyline>
											<line x1="12" y1="15" x2="12" y2="3"></line>
										</svg>
									</div>
									<div className="placeholder-text">Upload Image</div>
								</label>
							)}
							<input
								type="file"
								id="image"
								name="image"
								accept="image/*"
								onChange={handleImageChange}
								className="hidden-input"
							/>
						</div>
					</div>

					<div className="form-group">
						<label htmlFor="title">Title</label>
						<input
							type="text"
							id="title"
							name="title"
							value={formData.title}
							onChange={handleChange}
							required
						/>
					</div>

					<div className="form-group">
						<label htmlFor="description">Description</label>
						<ReactQuill
							theme="snow"
							value={formData.description}
							onChange={handleQuillChange}
							modules={modules}
							formats={formats}
							placeholder="Enter webinar description"
							className="custom-quill"
						/>
					</div>
					<div className="form-group-time">
						<div className="form-group">
							<label htmlFor="date">Date</label>
							<DatePicker
								selected={formData.date}
								onChange={handleDateChange}
								dateFormat="MMMM d, yyyy"
								minDate={new Date()}
								required
							/>
						</div>
						<div className="form-group">
							<label htmlFor="duration">Duration (min.)</label>
							<input
								type="number"
								id="duration"
								name="duration"
								value={formData.duration}
								onChange={handleChange}
								min="1"
								required
							/>
						</div>
					</div>

					<div className="form-group-time">
						<div className="form-group">
							<label htmlFor="startTime">Start Time</label>
							<DatePicker
								selected={formData.startTime}
								onChange={handleTimeChange}
								showTimeSelect
								showTimeSelectOnly
								timeIntervals={15}
								timeCaption="Time"
								dateFormat="h:mm aa"
								required
							/>
						</div>
						<div className="form-group">
							<label htmlFor="endTime">End Time</label>
							<DatePicker
								selected={formData.endTime}
								readOnly
								showTimeSelect
								showTimeSelectOnly
								timeIntervals={15}
								timeCaption="Time"
								dateFormat="h:mm aa"
								className="readonly-picker"
							/>
						</div>
					</div>

					<div className="form-group">
						<label htmlFor="privacy">Privacy</label>
						<select
							id="webinarType"
							name="webinarType"
							value={formData.webinarType}
							onChange={handleChange}
						>
							<option value="Private">Private</option>
							<option value="Public">Public</option>
							<option value="Pitch Day">Pitch Day</option>
						</select>
					</div>
					<div className="form-group">
						<label htmlFor="price">Price</label>
						<input
							type="number"
							id="price"
							name="price"
							value={formData.price}
							onChange={handleChange}
						/>
					</div>

					<div className="form-group">
						<label htmlFor="discount">Discount (%)</label>
						<input
							type="number"
							id="discount"
							name="discount"
							value={formData.discount}
							onChange={handleChange}
						/>
					</div>
				</form>
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button variant="primary" onClick={() => onSubmit(formData)}>
					{isEditing ? 'Save Changes' : 'Create Webinar'}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default WebinarModal;
